import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ContextIcon } from '@/shared/components/ui'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { useAuth } from '@/shared/contexts/AuthContext'
import { useUnit } from '@/modules/units/contexts/UnitContext'
import {
  deleteDocumentBatch,
  deleteOrphanDocuments,
  getDocumentDownloadUrl,
  listDocumentBatches,
  listOrphanDocuments,
  uploadDocuments,
  type DocumentBatchWithDocuments,
  type DocumentUploadProgress,
  type StoredDocument,
} from '@/modules/documents'

function UploadIcon() {
  return (
    <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" />
    </svg>
  )
}

function HistoryIcon() {
  return (
    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5m4-3v6l4 2" />
    </svg>
  )
}

function DownloadIcon() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v12m0 0 4-4m-4 4-4-4M5 21h14" />
    </svg>
  )
}

export function AccountingClientPortalDashboard() {
  const { t, locale } = useTranslation()
  const { user } = useAuth()
  const { currentUnit, userRole } = useUnit()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [batches, setBatches] = useState<DocumentBatchWithDocuments[]>([])
  const [orphanDocuments, setOrphanDocuments] = useState<StoredDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [deletingBatchId, setDeletingBatchId] = useState<string | null>(null)
  const [cleaningOrphans, setCleaningOrphans] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [progress, setProgress] = useState<DocumentUploadProgress | null>(null)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const loadBatches = useCallback(async () => {
    if (!currentUnit?.id) return

    setLoading(true)
    try {
      const [loadedBatches, loadedOrphans] = await Promise.all([
        listDocumentBatches(currentUnit.id),
        listOrphanDocuments(currentUnit.id),
      ])
      setBatches(loadedBatches)
      setOrphanDocuments(loadedOrphans)
    } catch (error) {
      console.error('[AccountingClientPortal] Error loading batches:', error)
      setMessage({ type: 'error', text: t('accounting.clientPortal.errors.load') })
    } finally {
      setLoading(false)
    }
  }, [currentUnit?.id, t])

  useEffect(() => {
    void loadBatches()
  }, [loadBatches])

  const summary = useMemo(() => {
    const documents = batches.flatMap((batch) => batch.documents || [])
    return {
      sent: documents.length,
      processing: batches.filter((batch) => ['received', 'processing'].includes(batch.status)).length,
      completed: documents.filter((document) => document.status === 'processed').length,
    }
  }, [batches])

  const handleFiles = async (files: File[]) => {
    if (!currentUnit?.id || files.length === 0 || uploading) return

    setUploading(true)
    setMessage(null)
    setProgress(null)

    try {
      const result = await uploadDocuments(currentUnit.id, files, setProgress)
      setMessage({
        type: result.rejected > 0 ? 'error' : 'success',
        text: t('accounting.clientPortal.upload.result', {
          accepted: String(result.accepted),
          duplicates: String(result.duplicates),
          rejected: String(result.rejected),
        }),
      })
      await loadBatches()
    } catch (error) {
      console.error('[AccountingClientPortal] Upload error:', error)
      const code = error instanceof Error ? error.message : 'upload_failed'
      const translated = t(`accounting.clientPortal.errors.${code}`)
      setMessage({
        type: 'error',
        text: translated.startsWith('accounting.') ? t('accounting.clientPortal.errors.upload') : translated,
      })
    } finally {
      setUploading(false)
      setProgress(null)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const handleDownload = async (path: string) => {
    try {
      window.open(await getDocumentDownloadUrl(path), '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('[AccountingClientPortal] Download error:', error)
      setMessage({ type: 'error', text: t('accounting.clientPortal.errors.download') })
    }
  }

  const handleDeleteBatch = async (batch: DocumentBatchWithDocuments) => {
    if (deletingBatchId || uploading) return

    const confirmed = window.confirm(t('accounting.clientPortal.history.deleteConfirm', {
      name: batch.original_name || t('accounting.clientPortal.history.unnamedBatch'),
    }))
    if (!confirmed) return

    setDeletingBatchId(batch.id)
    setMessage(null)

    try {
      await deleteDocumentBatch(batch.id)
      setBatches((current) => current.filter((item) => item.id !== batch.id))
      setMessage({ type: 'success', text: t('accounting.clientPortal.history.deleteSuccess') })
    } catch (error) {
      console.error('[AccountingClientPortal] Delete batch error:', error)
      setMessage({
        type: 'error',
        text: error instanceof Error && error.message === 'document_already_booked'
          ? t('accounting.clientPortal.errors.document_already_booked')
          : t('accounting.clientPortal.errors.delete'),
      })
    } finally {
      setDeletingBatchId(null)
    }
  }

  const handleCleanOrphans = async () => {
    if (!currentUnit?.id || cleaningOrphans) return
    if (!window.confirm(t('accounting.clientPortal.orphans.deleteConfirm', {
      count: String(orphanDocuments.length),
    }))) return

    setCleaningOrphans(true)
    setMessage(null)
    try {
      const deleted = await deleteOrphanDocuments(currentUnit.id)
      setOrphanDocuments([])
      setMessage({
        type: 'success',
        text: t('accounting.clientPortal.orphans.deleteSuccess', { count: String(deleted) }),
      })
    } catch (error) {
      console.error('[AccountingClientPortal] Orphan cleanup error:', error)
      setMessage({ type: 'error', text: t('accounting.clientPortal.errors.delete') })
    } finally {
      setCleaningOrphans(false)
    }
  }

  const formatDate = (value: string) => new Intl.DateTimeFormat(locale, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value))

  return (
    <div className="mx-auto flex max-w-screen-xl flex-col gap-6 p-4 lg:p-6">
      <header className="flex items-center gap-4">
        <div className="rounded-xl bg-blue-100 p-3 dark:bg-blue-900/30">
          <ContextIcon context="accounting" className="h-8 w-8 text-blue-700 dark:text-blue-400" />
        </div>
        <div>
          <p className="text-sm font-medium text-blue-700 dark:text-blue-400">
            {t('accounting.clientPortal.environmentLabel')}
          </p>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
            {currentUnit?.name}
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {t('accounting.clientPortal.subtitle')}
          </p>
        </div>
      </header>

      {message && (
        <div className={`rounded-xl border p-4 text-sm ${
          message.type === 'success'
            ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300'
            : 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300'
        }`}>
          {message.text}
        </div>
      )}

      {orphanDocuments.length > 0 && (
        <div className="flex flex-col justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200 sm:flex-row sm:items-center">
          <div>
            <p className="font-semibold">{t('accounting.clientPortal.orphans.title')}</p>
            <p className="mt-1 text-xs">{t('accounting.clientPortal.orphans.description', { count: String(orphanDocuments.length) })}</p>
          </div>
          <button
            type="button"
            disabled={cleaningOrphans}
            onClick={() => void handleCleanOrphans()}
            className="shrink-0 rounded-lg bg-amber-600 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {cleaningOrphans
              ? t('accounting.clientPortal.orphans.deleting')
              : t('accounting.clientPortal.orphans.delete')}
          </button>
        </div>
      )}

      <section className="grid gap-4 md:grid-cols-3">
        {(['sent', 'processing', 'completed'] as const).map((status) => (
          <article key={status} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
              {t(`accounting.clientPortal.summary.${status}`)}
            </p>
            <p className="mt-2 text-3xl font-semibold text-slate-900 dark:text-white">{summary[status]}</p>
          </article>
        ))}
      </section>

      <section className="grid gap-6 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
        <article
          onDragEnter={(event) => { event.preventDefault(); if (!uploading) setDragging(true) }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={(event) => {
            event.preventDefault()
            if (!event.currentTarget.contains(event.relatedTarget as Node)) setDragging(false)
          }}
          onDrop={(event) => {
            event.preventDefault()
            setDragging(false)
            void handleFiles(Array.from(event.dataTransfer.files))
          }}
          className={`rounded-2xl border border-dashed p-8 text-center transition ${
            dragging
              ? 'border-blue-500 bg-blue-100/80 dark:border-blue-500 dark:bg-blue-950/40'
              : 'border-blue-300 bg-blue-50/60 dark:border-blue-800 dark:bg-blue-950/20'
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xml,.txt,.zip,application/xml,text/xml,text/plain,application/zip"
            multiple
            disabled={uploading}
            onChange={(event) => void handleFiles(Array.from(event.target.files || []))}
            className="hidden"
          />
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
            <UploadIcon />
          </div>
          <h2 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">
            {uploading ? t('accounting.clientPortal.upload.uploading') : t('accounting.clientPortal.upload.title')}
          </h2>
          <p className="mx-auto mt-2 max-w-lg text-sm text-slate-600 dark:text-slate-400">
            {progress
              ? t('accounting.clientPortal.upload.progress', {
                  current: String(progress.current),
                  total: String(progress.total),
                  name: progress.fileName,
                })
              : t('accounting.clientPortal.upload.description')}
          </p>
          <button
            type="button"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            className="mt-5 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('accounting.clientPortal.upload.select')}
          </button>
          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
            {t('accounting.clientPortal.upload.hint')}
          </p>
        </article>

        <article className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <div className="flex items-center gap-3">
            <span className="text-slate-400"><HistoryIcon /></span>
            <h2 className="font-semibold text-slate-900 dark:text-white">
              {t('accounting.clientPortal.history.title')}
            </h2>
          </div>

          {loading ? (
            <div className="flex min-h-48 items-center justify-center">
              <div className="h-7 w-7 animate-spin rounded-full border-4 border-blue-600 border-r-transparent" />
            </div>
          ) : batches.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {t('accounting.clientPortal.history.emptyTitle')}
              </p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {t('accounting.clientPortal.history.emptyDescription')}
              </p>
            </div>
          ) : (
            <ul className="mt-5 max-h-[32rem] space-y-3 overflow-y-auto pr-1">
              {batches.map((batch) => (
                <li key={batch.id} className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                        {batch.original_name || t('accounting.clientPortal.history.unnamedBatch')}
                      </p>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {formatDate(batch.created_at)} · {t(`accounting.clientPortal.status.${batch.status}`)}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {batch.original_storage_path && (
                        <button
                          type="button"
                          onClick={() => void handleDownload(batch.original_storage_path!)}
                          title={t('accounting.clientPortal.history.downloadOriginal')}
                          className="rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-blue-600 dark:hover:bg-slate-800"
                        >
                          <DownloadIcon />
                        </button>
                      )}
                      {!batch.documents?.some((document) => ['processed', 'purged'].includes(document.status))
                        && (batch.created_by === user?.id || userRole === 'admin' || userRole === 'manager') && (
                        <button
                          type="button"
                          disabled={deletingBatchId === batch.id}
                          onClick={() => void handleDeleteBatch(batch)}
                          title={t('accounting.clientPortal.history.delete')}
                          className="rounded-md p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-rose-900/20"
                        >
                          {deletingBatchId === batch.id ? (
                            <span className="block h-4 w-4 animate-spin rounded-full border-2 border-rose-500 border-r-transparent" />
                          ) : (
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 7h12m-10 0 .7 12h6.6L16 7m-6 0V4h4v3" />
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                      {t('accounting.clientPortal.history.accepted', { count: String(batch.accepted_count) })}
                    </span>
                    {batch.duplicate_count > 0 && (
                      <span className="rounded-full bg-amber-100 px-2.5 py-1 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                        {t('accounting.clientPortal.history.duplicates', { count: String(batch.duplicate_count) })}
                      </span>
                    )}
                    {batch.rejected_count > 0 && (
                      <span className="rounded-full bg-rose-100 px-2.5 py-1 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
                        {t('accounting.clientPortal.history.rejected', { count: String(batch.rejected_count) })}
                      </span>
                    )}
                  </div>
                  {batch.documents?.length > 0 && (
                    <details className="mt-3 text-xs text-slate-600 dark:text-slate-400">
                      <summary className="cursor-pointer font-medium">
                        {t('accounting.clientPortal.history.viewFiles')}
                      </summary>
                      <ul className="mt-2 space-y-1.5 border-l border-slate-200 pl-3 dark:border-slate-700">
                        {batch.documents.map((document) => (
                          <li key={document.id} className="flex items-center justify-between gap-2">
                            <span className="truncate">{document.original_name}</span>
                            {document.storage_path && (
                              <button
                                type="button"
                                onClick={() => void handleDownload(document.storage_path!)}
                                className="shrink-0 font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400"
                              >
                                {t('accounting.clientPortal.history.download')}
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </li>
              ))}
            </ul>
          )}
        </article>
      </section>
    </div>
  )
}
